import Data from 'data';
import { defaultSummon } from 'logic/monster/summons';
import { AuraFlags, skillAuraStats, skillCalc, skillFormula } from 'logic/skills/calc';
import skillFunctions from '.';
import { DamageCalculator, missileDamage } from '../damage';
import { defaultDPS } from '../dps';

const functions = {};

// Curses

functions[30] = {
  buff(id, stats, level) {
    const skill = Data.skills[id];
    return {
      level,
      filter: skill.aurafilter | AuraFlags.OnlyAttackable | AuraFlags.NotInsideTown | AuraFlags.UseLineOfSight | AuraFlags.OnlySelectable | AuraFlags.IgnoreAllies | AuraFlags.NotInsideTown2,
      targetState: skill.auratargetstate,
      targetStats: () => skillAuraStats(skill, stats, level),
      targetCheck: (id, stats) => !stats.base.curse_immune,
    };
  },
};

// Raise Skeleton/Skeletal Mage

functions[31] = {
  summon(id, stats, level, target) {
    const resist = stats.base.passive_summon_resist || 0;
    return defaultSummon(id, stats, level, target, {
      stats: {
        skill89: {
          fireresist: resist,
          coldresist: resist,
          lightresist: resist,
          poisonresist: resist,
        },
      },
    });
  },
  dps(id, stats, level, target, uselessInfo, allResults, allStats) {
    const skill = Data.skills[id];
    const unit = skill.pettype.toLowerCase();
    const monster = Data.monsters[skill.summon];
    const sumskill = monster.skill1?.toString() || '0';
    const sumskData = Data.skills[sumskill];
    const info = allResults[unit]?.skills.find(sk => sk.id === sumskill)?.info;
    const sumStats = allStats[unit];
    if (!info || !sumskData || !sumStats) return;
    const func = skillFunctions[sumskData.srvdofunc || 0];
    if (!func) return;
    const modinfo = {...info};
    modinfo.animation = {...info.animation};
    modinfo.animation.hits = (modinfo.animation.hits || 1) * skillCalc(skill, skill.petmax, stats, level);
    return (func.dps || defaultDPS).call(func, sumskill, sumStats, sumStats.skills[sumskill] || 1, target, modinfo);
  },
};

// Poison Dagger

functions[32] = {
  damage(skill, stats, weapon, level, target) {
    if (!weapon) return null;
    const calc = new DamageCalculator(stats, weapon, false, true);
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.skillbonus('damagepercent', skillFormula(skill, 'clc1', stats, level));
    calc.convert(skill.etype, skillFormula(skill, 'clc4', stats, level));
    calc.srcdam(skill.srcdam);
    calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));
    return calc.calculate(target);
  },
};

// Psychic Hammer

functions[33] = {
  damage(skill, stats, weapon, level, target) {
    const calc = new DamageCalculator(stats, weapon, false, false);
    calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
      skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    calc.physical(skillFormula(skill, 'pdns', stats, level), skillFormula(skill, 'pdxs', stats, level));
    return calc.calculate(target);
  },
};

// Assassin charge-up skills (Tiger Strike/Cobra Strike/Phoenix Strike)

functions[34] = {
  showActive: id => !!Data.skills[id].aurastate,
  active: {
    charges: 0,
  },
  aura(id, stats, level, active) {
    const charges = (active || this.active).charges;
    if (!charges) return;
    const skill = Data.skills[id];
    return {
      level,
      state: skill.aurastate,
      stats: () => {
        const result = skillAuraStats(skill, stats, level);
        for (const id in result) {
          result[id] = (result[id] || 1) * charges;
        }
        if (result.progressive_tohit) {
          result['progressive#item_tohit_percent'] = result.progressive_tohit;
        }
        if (skill.prgdam === 1) {
          result['progressive#damagepercent'] = skillFormula(skill, 'clc1', stats, level) * charges;
        }
        if (skill.prgdam === 2) {
          const value = skillFormula(skill, 'ln12', stats, level);
          result['progressive#lifedrainmindam'] = (charges === 3 ? value * 2 : value);
        }
        if (skill.prgdam === 3 || skill.prgdam === 4) {
          switch (skill.etype) {
          case 'fire':
            result['progressive#firemindam'] = skillFormula(skill, 'edns', stats, level);
            result['progressive#firemaxdam'] = skillFormula(skill, 'edxs', stats, level);
            break;
          case 'cold':
            result['progressive#coldmindam'] = skillFormula(skill, 'edns', stats, level);
            result['progressive#coldmaxdam'] = skillFormula(skill, 'edxs', stats, level);
            result['progressive#coldlength'] = skillFormula(skill, 'edln', stats, level);
            break;
          case 'ltng':
            result['progressive#lightmindam'] = skillFormula(skill, 'edns', stats, level);
            result['progressive#lightmaxdam'] = skillFormula(skill, 'edxs', stats, level);
            break;
          case 'pois':
            result['progressive#poisonmindam'] = skillFormula(skill, 'edns', stats, level);
            result['progressive#poisonmaxdam'] = skillFormula(skill, 'edxs', stats, level);
            result['progressive#poisonlength'] = skillFormula(skill, 'edln', stats, level);
            result['progressive#poison_count'] = 1;
            break;
          case 'mag':
            result['progressive#magicmindam'] = skillFormula(skill, 'edns', stats, level);
            result['progressive#magicmaxdam'] = skillFormula(skill, 'edxs', stats, level);
            break;
          // no default
          }
        }
        if (skill.prgdam === 4) {
          result[`progressive#convert_${skill.etype}`] = skillFormula(skill, 'clc1', stats, level);
        }
        result[`_skill${id}`] = charges;
        return result;
      },
    };
  },
  damage(skill, stats, weapon, level, target) {
    const calc = new DamageCalculator(stats, weapon, false, true);
    calc.srcdam(128);
    calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));

    const damage = {damage: calc.calculate(target)};

    const extra = {};
    if (skill.srvdofunc === 34) extra.fireFrames = skillFormula(skill, 'ln34', stats, level);

    const charges = stats.base[`_skill${skill.id}`] || 0;
    for (let i = 1; i <= charges; ++i) {
      const name = skill['srvmissile' + 'abc'[i - 1]];
      const missile = name && missileDamage(Data.missileByName[name], stats, stats.weapon1, level, target, extra);
      if (missile) {
        for (const label in missile) {
          if (missile[label]) {
            missile[label].label = label;
            missile[label].charge = i;
            damage[`charge${i}_${label}`] = missile[label];
          }
        }
      }
    }

    return damage;
  },
};

// Assassin charge-up skills (Fists of Fire, Claws of Thunder, Blades of Ice)

functions[35] = {
  showActive: id => !!Data.skills[id].aurastate,
  active: {
    charges: 0,
  },
  aura: functions[34].aura,
  damage: functions[34].damage,
};

export default functions;
